import { Theme, ThemeOptions, darken, lighten } from '@mui/material/styles';

// Map Service IDs to Theme Keys (below)
const serviceIdToThemeKeyMap: Record<string, string> = {
  '2169': 'mudflap_theme',
  '1179': 'mudflap_theme',
  '2103': 'truckstop_theme',
  '1707': 'truckstop_theme',
  '123': 'green_theme',

  // AtoB
  '320': 'atob_theme',

  // revenova prod
  '1014': 'revenova_theme',
  '981': 'revenova_theme',
  '2136': 'revenova_theme',
  '1872': 'revenova_theme',
  '1806': 'revenova_theme',
  '1839': 'revenova_theme',
  '2004': 'revenova_theme',
  '2566': 'revenova_theme',

  // these are services not live in production
  '1': 'atob_theme',
  '2': 'mudflap_theme',
  '3': 'truckstop_theme',
  // '3': 'mudflap_theme',
  // '3': 'infios_theme',
  // '3': 'revenova_theme',
  // '3': 'green_theme',
  '6': 'truckstop_theme',
  '7': 'mudflap_theme',
  '42': 'mudflap_theme',
  '75': 'truckstop_theme',
  '108': 'revenova_theme',
  '141': 'truckstop_theme',
  '174': 'mudflap_theme',
  '207': 'truckstop_theme',
};

// Helper function to convert hex to rgba
function hexToRgba(hex: string, opacity: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Define a safe color getter helper function to avoid TypeScript errors
function getPrimaryMainColor(theme: Record<string, any>): string {
  return theme?.palette?.primary?.main || '#FF0000';
}

function getActionColor(
  theme: Record<string, any>,
  type: string,
  fallback: string
): string {
  return theme?.palette?.action?.[type] || fallback;
}

function getTextColor(
  theme: Record<string, any>,
  type: string,
  fallback: string
): string {
  return theme?.palette?.text?.[type] || fallback;
}

function getErrorColor(theme: Record<string, any>): string {
  return theme?.palette?.error?.main || '#D32F2F';
}

// Simplify component style function to avoid TypeScript errors
// We're explicitly casting to 'any' here to bypass TypeScript's strict checks
// This is not ideal but will work for this specific use case
const styleFunc = (callback: Function): any => callback;

// Define the structure for custom theme properties
export interface CustomThemeProps {
  backgroundImage?: string;
  buttonTextColor?: string;
  serviceLogo?: string; // Path to service-specific logo for the logo section
  skipConfirmation?: {
    valueProp?: string; // Custom value proposition text for skip confirmation dialog
  };
  avatarColors?: {
    background: string;
    icon: string;
  };
  footer?: {
    background: string;
    text: string;
  };
  branding?: {
    poweredBy: boolean;
    poweredByText?: string;
    logoSrc?: string;
    logoAlt?: string;
    logoHeight?: string;
    logoUrl?: string;
    textColor?: string; // Color for the "Powered by" text
  };
  onboardErrorAlert?: {
    border: string;
    background: string;
    textColor: string;
  };
  inputField?: {
    focus: {
      borderColor: string;
      borderWidth: string;
    };
    error: {
      borderColor: string;
      borderWidth: string;
    };
    hover: {
      borderColor: string;
    };
  };
  buttons?: {
    outlined: {
      active: {
        borderColor: string;
        textColor: string;
        backgroundColor: string;
      };
      hover: {
        borderColor: string;
        textColor: string;
        backgroundColor: string;
      };
      focus: {
        borderColor: string;
        textColor: string;
        backgroundColor: string;
        boxShadow: string;
      };
      press: {
        borderColor: string;
        textColor: string;
        backgroundColor: string;
      };
      disabled: {
        borderColor: string;
        textColor: string;
        backgroundColor: string;
      };
    };
    text: {
      active: {
        textColor: string;
        backgroundColor: string;
      };
      hover: {
        textColor: string;
        backgroundColor: string;
      };
      focus: {
        textColor: string;
        backgroundColor: string;
        boxShadow: string;
      };
      press: {
        textColor: string;
        backgroundColor: string;
      };
      disabled: {
        textColor: string;
        backgroundColor: string;
      };
    };
  };
}

// Extend MUI's ThemeOptions with custom properties
export interface ServiceThemeOptions extends ThemeOptions {
  custom?: CustomThemeProps;
}

// Define the default theme
export const defaultTheme: ServiceThemeOptions = {
  palette: {
    primary: {
      main: '#FF0000', // Axle Red
      light: '#FFCDD2', // Material Red 100
      dark: darken('#FF0000', 0.2),
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0000FF', // Example: Blue
      light: lighten('#0000FF', 0.2),
      dark: darken('#0000FF', 0.2),
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D32F2F', // Material Red 700 (Standard Error)
      light: '#EF9A9A', // Material Red 200
      dark: darken('#D32F2F', 0.2),
    },
    warning: {
      main: '#FE9659', // Orange
      light: '#FFE0B2', // Material Orange 100
      dark: darken('#FE9659', 0.2),
    },
    info: {
      // Added for informational text/buttons
      main: '#0288d1', // MUI standard info blue
      light: '#03a9f4',
      dark: '#01579b',
    },
    success: {
      main: '#4CAF50', // Standard success green
      light: '#81C784',
      dark: '#388E3C',
    },
    text: {
      primary: '#333333', // Dark grey for main text
      secondary: '#555555', // Lighter grey for secondary text
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    divider: '#E0E0E0', // Light gray for dividers
    action: {
      active: '#000000',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: '#C7CACB',
      disabledBackground: '#E5E6E7',
      focus: 'rgba(0, 0, 0, 0.12)',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32px',
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '24px',
    },
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    caption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '15px',
      letterSpacing: '-0.15px',
    },
    button: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '16px',
      letterSpacing: '0.75px',
      textTransform: 'uppercase',
    },
  },
  custom: {
    buttonTextColor: '#FFFFFF', // White text on colored buttons
    skipConfirmation: {
      valueProp:
        "Connecting your telematics data unlocks services tailored to your fleet's needs — from smarter operations to improved bottom-line results.",
    },
    avatarColors: {
      background: '#F4F5F5', // Light gray for avatars
      icon: '#5F6368', // Medium gray for icons
    },
    footer: {
      background: '#EEEFEF', // Light gray background
      text: '#333333', // Dark gray text (matching defaultTheme text.primary)
    },
    branding: {
      poweredBy: true,
      poweredByText: 'Powered by',
      logoSrc: 'logo/catena_logo.png',
      logoAlt: 'Catena',
      logoHeight: '3vh',
      logoUrl: 'https://www.catenaclearing.io',
      textColor: '#333333', // Default to match theme text color
    },
    onboardErrorAlert: {
      border: '#fd9727',
      background: '#fff3e5',
      textColor: '#000000',
    },
    inputField: {
      focus: {
        borderColor: '#FF0000', // Primary red color
        borderWidth: '2px',
      },
      error: {
        borderColor: '#D32F2F', // Error red color
        borderWidth: '2px',
      },
      hover: {
        borderColor: '#333333', // Text primary color
      },
    },
    buttons: {
      outlined: {
        active: {
          borderColor: '#C7CACB',
          textColor: '#FF0000',
          backgroundColor: 'transparent',
        },
        hover: {
          borderColor: '#FF0000',
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.04)',
        },
        focus: {
          borderColor: '#FF0000',
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.04)',
          boxShadow: '0 0 0 2px rgba(255, 0, 0, 0.2)',
        },
        press: {
          borderColor: '#FF0000',
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.08)',
        },
        disabled: {
          borderColor: '#E0E0E0',
          textColor: '#C7CACB',
          backgroundColor: 'transparent',
        },
      },
      text: {
        active: {
          textColor: '#FF0000',
          backgroundColor: 'transparent',
        },
        hover: {
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.08)',
        },
        focus: {
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.08)',
          boxShadow: '0 0 0 2px rgba(255, 0, 0, 0.2)',
        },
        press: {
          textColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.12)',
        },
        disabled: {
          textColor: '#C7CACB',
          backgroundColor: 'transparent',
        },
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          borderRadius: '8px',
          transition: 'background-color 0.3s, box-shadow 0.3s',
          '&:hover': {
            backgroundColor: darken(getPrimaryMainColor(theme), 0.1),
          },
          '&:active': {
            backgroundColor: darken(getPrimaryMainColor(theme), 0.2),
            boxShadow: 'none',
          },
          '&:focus': {
            boxShadow: `0 0 0 3px ${hexToRgba(
              getPrimaryMainColor(theme),
              0.3
            )}`,
          },
          '&.Mui-disabled': {
            backgroundColor: getActionColor(
              theme,
              'disabledBackground',
              '#E5E6E7'
            ),
            color: getActionColor(theme, 'disabled', '#C7CACB'),
            boxShadow: 'none',
          },
        })),
        outlined: styleFunc((theme: Theme) => ({
          '&:hover': {
            borderColor: getPrimaryMainColor(theme),
            backgroundColor: hexToRgba(getPrimaryMainColor(theme), 0.04),
          },
        })),
        text: styleFunc((theme: Theme) => ({
          '&:hover': {
            backgroundColor: hexToRgba(getPrimaryMainColor(theme), 0.04),
          },
        })),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          '&:hover': {
            backgroundColor: hexToRgba(
              getActionColor(theme, 'active', '#000000'),
              0.04
            ),
          },
          '&:active': {
            backgroundColor: hexToRgba(
              getActionColor(theme, 'active', '#000000'),
              0.08
            ),
          },
        })),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // example: boxShadow: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          borderRadius: '4px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: getTextColor(theme, 'primary', '#000000'),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: getPrimaryMainColor(theme),
            borderWidth: '1px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: getErrorColor(theme),
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: getActionColor(theme, 'disabled', '#E0E0E0'),
          },
        })),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          '&.Mui-focused': {
            color: getPrimaryMainColor(theme),
          },
        })),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          color: getTextColor(theme, 'secondary', '#757575'),
          '&.Mui-checked': {
            color: getPrimaryMainColor(theme),
          },
          '&:hover': {
            backgroundColor: hexToRgba(getPrimaryMainColor(theme), 0.04),
          },
        })),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          color: getTextColor(theme, 'secondary', '#757575'),
          '&.Mui-checked': {
            color: getPrimaryMainColor(theme),
          },
          '&:hover': {
            backgroundColor: hexToRgba(getPrimaryMainColor(theme), 0.04),
          },
        })),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: getPrimaryMainColor(theme),
            '&:hover': {
              backgroundColor: hexToRgba(getPrimaryMainColor(theme), 0.04),
            },
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: getPrimaryMainColor(theme),
          },
        })),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: getTextColor(theme, 'secondary', '#757575'),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: getPrimaryMainColor(theme),
          },
        })),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          overflow: 'hidden',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          backgroundColor: getPrimaryMainColor(theme),
          color: theme?.palette?.primary?.contrastText || '#FFFFFF',
        })),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '0 16px',
          '@media (min-width: 600px)': {
            padding: '0 24px',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          '&:hover': {
            backgroundColor: hexToRgba(
              getActionColor(theme, 'active', '#000000'),
              0.04
            ),
          },
        })),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: getPrimaryMainColor(theme),
          },
        })),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
        colorPrimary: styleFunc((theme: Theme) => ({
          backgroundColor: getPrimaryMainColor(theme),
          color: theme?.palette?.primary?.contrastText || '#FFFFFF',
        })),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: styleFunc((theme: Theme) => ({
          backgroundColor:
            theme?.custom?.avatarColors?.background ||
            theme?.palette?.grey?.[200] ||
            '#E0E0E0',
          color:
            theme?.custom?.avatarColors?.icon ||
            getTextColor(theme, 'secondary', '#757575'),
        })),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: styleFunc((theme: Theme) => ({
          backgroundColor: theme?.palette?.grey?.[800] || '#424242',
          fontSize: '12px',
          padding: '8px 12px',
          borderRadius: '4px',
        })),
      },
    },
  },
};

// Custom themes for each service / customer
export const serviceThemes: Record<string, ServiceThemeOptions> = {
  green_theme: {
    palette: {
      primary: { main: '#006400' },
      secondary: { main: '#FFD700' },
      text: { primary: '#2F4F4F', secondary: '#696969' },
      background: { default: '#F0F8FF' },
    },
    typography: {
      fontFamily: '"Times New Roman", Times, serif',
      h5: {
        fontWeight: 800,
        color: '#2F4F4F',
      },
      subtitle1: {
        color: '#2F4F4F',
      },
      body1: {
        color: '#696969',
      },
      caption: {
        color: '#2F4F4F',
      },
    },
    custom: {
      backgroundImage: 'url("/path/to/customer123-bg.jpg")',
      avatarColors: {
        background: '#F0F8FF',
        icon: '#006400',
      },
      footer: {
        background: '#E8F0E8',
        text: '#2F4F4F',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            '&:focus': {
              boxShadow: '0 0 0 3px rgba(0, 100, 0, 0.3)',
            },
          },
          contained: {
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#006400',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#006400',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#006400',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#2F4F4F',
            '&.Mui-checked': {
              color: '#006400',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: '#2F4F4F',
            '&.Mui-checked': {
              color: '#006400',
            },
          },
        },
      },
    },
  },

  // truckstop theme
  truckstop_theme: {
    palette: {
      primary: {
        main: '#B60207', // P900: Link colors, Primary buttons
      },
      secondary: {
        main: '#0275DF', // T900: Tertiary color
      },
      text: {
        primary: '#000000', // Pure black for title text
        secondary: '#586166', // S500: Alternative for low emphasis text
      },
      background: {
        default: 'transparent', // To allow body image
      },
      divider: '#DCDEDF', // S40: Panel border
      info: {
        main: '#0275DF',
      },
      success: {
        main: '#0F956D',
      },
      warning: {
        main: '#FD9726',
      },
    },
    typography: {
      fontFamily: 'Inter, Roboto, Arial, sans-serif',
      h5: {
        fontWeight: 800, // Extra bold per design spec
        color: '#000000', // Pure black
      },
      subtitle1: {
        color: '#263238', // Secondary/900
      },
      body1: {
        color: '#586166', // Secondary/500
      },
      caption: {
        color: '#263238', // Secondary/900
      },
    },
    custom: {
      backgroundImage: 'url("/theme_images/truckstop_background.png")',
      serviceLogo: '/theme_images/truckstop_logo.png',
      skipConfirmation: {
        valueProp: 'Brokers prioritize carriers with ELD visibility.',
      },
      avatarColors: {
        background: '#F4F5F5', // Secondary/10
        icon: '#5F6368',
      },
      footer: {
        background: '#EEEFEF', // Secondary/20
        text: '#263238', // Secondary/900
      },
      branding: {
        poweredBy: true,
        logoSrc: 'logo/catena_white_logo.png',
        textColor: '#FFFFFF', // White text for branding
      },
      onboardErrorAlert: {
        border: '#fd9727',
        background: '#fff3e5',
        textColor: '#000000',
      },
      inputField: {
        focus: {
          borderColor: '#000000',
          borderWidth: '2px',
        },
        error: {
          borderColor: '#FD9726',
          borderWidth: '2px',
        },
        hover: {
          borderColor: '#000000', // Pure black for truckstop
        },
      },
      buttons: {
        outlined: {
          active: {
            borderColor: '#C7CACB',
            textColor: '#B60207',
            backgroundColor: 'transparent',
          },
          hover: {
            borderColor: '#C7CACB',
            textColor: '#B60207',
            backgroundColor: '#FCF5F5',
          },
          focus: {
            borderColor: '#C7CACB',
            textColor: '#B60207',
            backgroundColor: 'rgba(182, 2, 7, 0.04)',
            boxShadow: '0 0 0 2px rgba(182, 2, 7, 0.2)',
          },
          press: {
            borderColor: '#C7CACB',
            textColor: '#B60207',
            backgroundColor: 'rgba(182, 2, 7, 0.08)',
          },
          disabled: {
            borderColor: '#E0E0E0',
            textColor: '#C7CACB',
            backgroundColor: 'transparent',
          },
        },
        text: {
          active: {
            textColor: '#B60207',
            backgroundColor: 'transparent',
          },
          hover: {
            textColor: '#B60207',
            backgroundColor: '#FCF5F5',
          },
          focus: {
            textColor: '#B60207',
            backgroundColor: 'rgba(182, 2, 7, 0.08)',
            boxShadow: '0 0 0 2px rgba(182, 2, 7, 0.2)',
          },
          press: {
            textColor: '#B60207',
            backgroundColor: 'rgba(182, 2, 7, 0.12)',
          },
          disabled: {
            textColor: '#C7CACB',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            '&:focus': {
              boxShadow: '0 0 0 3px rgba(182, 2, 7, 0.3)',
            },
          },
          contained: {
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#B60207',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#B60207',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#B60207',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#586166',
            '&.Mui-checked': {
              color: '#B60207',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: '#586166',
            '&.Mui-checked': {
              color: '#B60207',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            marginBottom: '8px',
          },
        },
      },
    },
  },

  // mudflap theme
  mudflap_theme: {
    palette: {
      primary: {
        main: '#12BA7F', // Vibrant Green
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#0946B4', // Deep Blue
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
      },
      background: {
        default: '#0946B4',
      },
      success: {
        main: '#12BA7F', // Aligning success color with primary
      },
    },

    typography: {
      fontFamily: 'Gilroy, Roboto, Arial, sans-serif',
      h5: {
        color: '#212121',
      },
      subtitle1: {
        color: '#212121',
      },
      body1: {
        color: '#757575',
      },
      caption: {
        color: '#212121',
      },
    },

    custom: {
      avatarColors: {
        background: '#E8F5F0',
        icon: '#12BA7F',
      },
      branding: {
        poweredBy: true,
        logoSrc: 'logo/catena_white_logo.png',
        textColor: '#FFFFFF', // White text for branding
      },
      onboardErrorAlert: {
        border: '#fd9727',
        background: '#fff3e5',
        textColor: '#000000',
      },
      inputField: {
        focus: {
          borderColor: '#12BA7F', // Mudflap primary green
          borderWidth: '2px',
        },
        error: {
          borderColor: '#D32F2F', // Standard error red
          borderWidth: '2px',
        },
        hover: {
          borderColor: '#212121', // Text primary color for mudflap
        },
      },
      buttons: {
        outlined: {
          active: {
            borderColor: '#C7CACB',
            textColor: '#12BA7F',
            backgroundColor: 'transparent',
          },
          hover: {
            borderColor: '#12BA7F',
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.04)',
          },
          focus: {
            borderColor: '#12BA7F',
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.04)',
            boxShadow: '0 0 0 2px rgba(18, 186, 127, 0.2)',
          },
          press: {
            borderColor: '#12BA7F',
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.08)',
          },
          disabled: {
            borderColor: '#E0E0E0',
            textColor: '#C7CACB',
            backgroundColor: 'transparent',
          },
        },
        text: {
          active: {
            textColor: '#12BA7F',
            backgroundColor: 'transparent',
          },
          hover: {
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.08)',
          },
          focus: {
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.08)',
            boxShadow: '0 0 0 2px rgba(18, 186, 127, 0.2)',
          },
          press: {
            textColor: '#12BA7F',
            backgroundColor: 'rgba(18, 186, 127, 0.12)',
          },
          disabled: {
            textColor: '#C7CACB',
            backgroundColor: 'transparent',
          },
        },
      },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            textTransform: 'none',
            color: '#FFFFFF',
            backgroundColor: '#12BA7F',
            '&:focus': {
              boxShadow: '0 0 0 3px rgba(18, 186, 127, 0.3)',
            },
          },
          contained: {
            color: '#FFFFFF',
            backgroundColor: '#12BA7F',
          },
          outlined: {
            borderColor: '#12BA7F',
            color: '#12BA7F',
          },
          text: {
            color: '#12BA7F',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: '#12BA7F',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#12BA7F',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#12BA7F',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#12BA7F',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#12BA7F',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: '#12BA7F',
            },
          },
        },
      },
    },
  },

  // infios theme
  infios_theme: {
    palette: {
      primary: {
        main: '#D1FF1C', // Neon green as primary brand color
        contrastText: '#1F2937', // Dark text on green background
      },
      secondary: {
        main: '#1F2937', // Dark navy as secondary
      },
      text: {
        primary: '#1F2937',
        secondary: '#4B5563',
      },
      background: {
        default: '#FFFFFF',
      },
      divider: '#E5E7EB',
      info: {
        main: '#D1FF1C',
        contrastText: '#1F2937',
      },
      success: {
        main: '#34D399', // Emerald green (if needed)
      },
      warning: {
        main: '#FBBF24', // Amber (if needed)
      },
    },
    typography: {
      fontFamily: '"Aeonik Pro", "Inter", "Helvetica Neue", Arial, sans-serif',
      h5: {
        fontWeight: 600,
        fontSize: '28px',
        color: '#1F2937',
      },
      subtitle1: {
        fontWeight: 500,
        color: '#4B5563',
      },
      body1: {
        color: '#4B5563',
      },
      caption: {
        color: '#6B7280',
      },
      button: {
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'none',
        letterSpacing: '0.25px',
      },
    },
    custom: {
      backgroundImage: 'url("/theme_images/infios_background.png")',
      avatarColors: {
        background: '#F9FAFB',
        icon: '#6B7280',
      },
      footer: {
        background: '#F3F4F6',
        text: '#1F2937',
      },
      branding: {
        poweredBy: true,
        textColor: '#FFFFFF',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '4px', // Smaller radius like Infios
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '14px',
            padding: '12px 24px',
            '&:focus': {
              boxShadow: '0 0 0 2px rgba(209, 255, 28, 0.4)',
            },
          },
          contained: {
            backgroundColor: '#D1FF1C', // Solid neon green
            color: '#1F2937', // Dark text on green
            boxShadow: 'none', // Clean, flat design
            border: 'none',
            '&:hover': {
              backgroundColor: '#B8E619', // Slightly darker on hover
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: '#A3D117',
            },
          },
          outlined: {
            borderColor: '#D1FF1C',
            color: '#D1FF1C',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#D1FF1C',
              color: '#1F2937',
              borderColor: '#D1FF1C',
            },
          },
          text: {
            color: '#D1FF1C',
            '&:hover': {
              backgroundColor: 'rgba(209, 255, 28, 0.1)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '4px', // Match button radius
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1FF1C',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1FF1C',
              borderWidth: '2px',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#D1FF1C',
              fontWeight: 500,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#4B5563',
            '&.Mui-checked': {
              color: '#D1FF1C',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: '#4B5563',
            '&.Mui-checked': {
              color: '#D1FF1C',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            marginBottom: '8px',
          },
        },
      },
    },
  },

  // revenova theme
  revenova_theme: {
    palette: {
      primary: {
        main: '#FF5951', // Red button color - matches palette
        contrastText: '#FFFFFF', // White text on red buttons
      },
      secondary: {
        main: '#0172BC', // Updated to exact blue from palette (was #2D71C1)
      },
      text: {
        primary: '#002F67', // Dark blue for main text - matches palette
        secondary: '#45556b', // Medium blue-gray for secondary text - from palette
      },
      background: {
        default: '#FFFFFF', // Clean white background
        paper: '#f3f8fc', // Very light blue from palette instead of pure white
      },
      divider: '#c1e6ff', // Light blue from palette instead of gray
      info: {
        main: '#0172BC', // Updated to match secondary (was #0072BC)
      },
      success: {
        main: '#10B981', // Keep existing green for success states
      },
      warning: {
        main: '#F59E0B', // Keep existing amber for warnings
      },
      error: {
        main: '#FF5951', // Use their red for errors too
      },
    },
    typography: {
      fontFamily: '"Albert Sans", "Inter", "Helvetica Neue", Arial, sans-serif',
      h5: {
        fontWeight: 700,
        fontSize: '28px',
        color: '#002742', // Updated to darkest blue from palette
      },
      subtitle1: {
        fontWeight: 600,
        color: '#002F67', // Keep existing dark blue
      },
      body1: {
        color: '#45556b', // Updated to medium blue-gray from palette
      },
      caption: {
        color: '#45556b', // Updated to medium blue-gray for consistency
      },
      button: {
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'none',
        letterSpacing: '0.25px',
      },
    },
    custom: {
      backgroundImage: 'url("/theme_images/revenova_background.jpg")',
      serviceLogo: '/theme_images/revenova_logo.png',
      skipConfirmation: {
        valueProp:
          'Connecting your telematics data to Revenova TMS enables real-time visibility and streamlined operations across your entire logistics workflow.',
      },
      avatarColors: {
        background: '#c1e6ff', // Updated to light blue from palette
        icon: '#0172BC', // Updated to match secondary color
      },
      footer: {
        background: '#9dc2e6', // Updated to light blue from palette
        text: '#002742', // Updated to darkest blue from palette
      },
      branding: {
        poweredBy: true,
        textColor: '#FFFFFF',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px', // Slightly rounded like their site
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '14px',
            padding: '12px 24px',
            '&:focus': {
              boxShadow: '0 0 0 2px rgba(255, 89, 81, 0.4)',
            },
          },
          contained: {
            backgroundColor: '#FF5951', // Red buttons
            color: '#FFFFFF',
            boxShadow:
              '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
            border: 'none',
            '&:hover': {
              backgroundColor: '#E04A42', // Darker red on hover
              boxShadow:
                '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
            },
            '&:active': {
              backgroundColor: '#D1423A',
            },
          },
          outlined: {
            borderColor: '#0172BC', // Updated to match secondary color
            color: '#0172BC', // Updated to match secondary color
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(1, 114, 188, 0.04)', // Updated rgba values
              borderColor: '#0172BC',
            },
          },
          text: {
            color: '#0172BC', // Updated to match secondary color
            '&:hover': {
              backgroundColor: 'rgba(1, 114, 188, 0.04)', // Updated rgba values
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0172BC', // Updated to match secondary color
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0172BC', // Updated to match secondary color
              borderWidth: '2px',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: '#0172BC', // Updated to match secondary color
              fontWeight: 500,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#45556b', // Updated to medium blue-gray from palette
            '&.Mui-checked': {
              color: '#0172BC', // Updated to match secondary color
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: '#45556b', // Updated to medium blue-gray from palette
            '&.Mui-checked': {
              color: '#0172BC', // Updated to match secondary color
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            marginBottom: '8px',
          },
        },
      },
    },
  },

  // AtoB theme
  atob_theme: {
    palette: {
      primary: {
        main: '#22C55E', // CTA green
        contrastText: '#0B1220', // Dark text on green
      },
      secondary: { main: '#0B1220' }, // Dark pill/nav & accents
      text: { primary: '#0B1220', secondary: '#4B5563' },
      background: { default: '#FFFFFF', paper: '#FAFAFA' },
      divider: '#E5E7EB',
      info: { main: '#0B1220' },
      success: { main: '#22C55E' },
      warning: { main: '#F59E0B' },
      error: { main: '#EF4444' }, // fine to keep for non-form UI (e.g., Alerts)
    },

    typography: {
      fontFamily:
        '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
      h5: {
        fontWeight: 800,
        fontSize: '28px',
        lineHeight: '34px',
        letterSpacing: '-0.2px',
        color: '#0B1220',
      },
      subtitle1: { fontWeight: 600, color: '#0B1220' },
      body1: { color: '#4B5563' },
      caption: { color: '#6B7280' },
      button: {
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'none',
        letterSpacing: '0.2px',
      },
    },

    custom: {
      backgroundImage: 'url("/theme_images/atob_background.png")',
      serviceLogo: '/theme_images/atob_logo.png',
      skipConfirmation: {
        valueProp:
          'Connect your GPS / ELD and unlock credit, extra savings and more.',
      },
      avatarColors: { background: '#E5E7EB', icon: '#0B1220' },
      footer: { background: '#F3F4F6', text: '#0B1220' },
      branding: { poweredBy: true, textColor: '#FFFFFF' },

      // Parity with other themes (no red borders anywhere on inputs)
      onboardErrorAlert: {
        border: '#F59E0B',
        background: '#FFF7ED',
        textColor: '#0B1220',
      },
      inputField: {
        focus: { borderColor: '#0B1220', borderWidth: '2px' }, // ← black/navy
        error: { borderColor: '#0B1220', borderWidth: '2px' }, // ← black/navy
        hover: { borderColor: '#0B1220' }, // ← black/navy
      },
      buttons: {
        // No red states; outlined/text are neutral/dark
        outlined: {
          active: {
            borderColor: '#0B1220',
            textColor: '#0B1220',
            backgroundColor: 'transparent',
          },
          hover: {
            borderColor: '#0B1220',
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.06)',
          },
          focus: {
            borderColor: '#0B1220',
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.06)',
            boxShadow: '0 0 0 2px rgba(11,18,32,0.15)',
          },
          press: {
            borderColor: '#0B1220',
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.10)',
          },
          disabled: {
            borderColor: '#E5E7EB',
            textColor: '#9CA3AF',
            backgroundColor: 'transparent',
          },
        },
        text: {
          active: { textColor: '#0B1220', backgroundColor: 'transparent' },
          hover: {
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.06)',
          },
          focus: {
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.06)',
            boxShadow: '0 0 0 2px rgba(11,18,32,0.15)',
          },
          press: {
            textColor: '#0B1220',
            backgroundColor: 'rgba(11,18,32,0.10)',
          },
          disabled: { textColor: '#9CA3AF', backgroundColor: 'transparent' },
        },
      },
    },

    components: {
      // Buttons: CTA is green; no red variants
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '9999px', // pill
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '14px',
            padding: '10px 20px',
            '&:focus': { boxShadow: '0 0 0 2px rgba(34,197,94,0.35)' }, // green focus ring
          },
          contained: {
            backgroundColor: '#22C55E',
            color: '#0B1220',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#16A34A',
              boxShadow:
                '0 4px 6px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
            },
            '&:active': { backgroundColor: '#15803D' },
          },
          outlined: {
            borderColor: '#0B1220',
            color: '#0B1220',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(11,18,32,0.06)',
              borderColor: '#0B1220',
            },
          },
          text: {
            color: '#0B1220',
            '&:hover': { backgroundColor: 'rgba(11,18,32,0.06)' },
          },
        },
      },

      // Inputs: force black/navy borders on hover/focus/error across variants
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0B1220',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0B1220',
              borderWidth: '2px',
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0B1220',
              borderWidth: '2px',
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            '&:before': { borderBottomColor: '#E5E7EB' },
            '&:hover:before': { borderBottomColor: '#0B1220' },
            '&.Mui-focused:before': { borderBottomColor: '#0B1220' },
            '&.Mui-error:before': { borderBottomColor: '#0B1220' },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            '&:before': { borderBottomColor: '#E5E7EB' },
            '&:hover:before': { borderBottomColor: '#0B1220' },
            '&.Mui-focused:before': { borderBottomColor: '#0B1220' },
            '&.Mui-error:before': { borderBottomColor: '#0B1220' },
          },
        },
      },

      // Labels & helper text: no red on error/focus
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': { color: '#0B1220', fontWeight: 600 },
            '&.Mui-error': { color: '#0B1220' },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { '&.Mui-error': { color: '#6B7280' } }, // subtle neutral helper on error
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: { color: '#6B7280', '&.Mui-checked': { color: '#22C55E' } },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: { color: '#6B7280', '&.Mui-checked': { color: '#22C55E' } },
        },
      },

      MuiPaper: { styleOverrides: { rounded: { borderRadius: '14px' } } },
      MuiListItemText: { styleOverrides: { primary: { marginBottom: '8px' } } },
    },
  },

  // More themes can be added here...
};

// Helper function to perform deep merge of theme objects
const deepMerge = (target: any, source: any): any => {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
};

// Helper to check if value is an object
const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

// Updated function to get the theme
export const getThemeByServiceId = (
  serviceId?: number
): ServiceThemeOptions => {
  if (serviceId) {
    const themeKey = serviceIdToThemeKeyMap[String(serviceId)];
    if (themeKey && serviceThemes[themeKey]) {
      const specificTheme = serviceThemes[themeKey];

      // Deep merge the default theme with the service-specific theme
      return deepMerge(defaultTheme, specificTheme);
    }
  }
  return defaultTheme;
};
